/**
 * K4b „Die nächste Rechnung jedes laufenden Vertrags" (P2-V3 § 2.3, F4 Teilung der Prognose-Karte): Kacheln für
 * Verträge, nächste Rechnungen (Summe, Ø je Vertrag), davon voraussichtlich bezahlt und — F5 — den Zahlungseingang
 * dieses Quartals gegen Plan (nur %, eigene Route, lädt getrennt: `computeCashflowHistory` braucht kalt 12–19 s).
 * Darunter die Tabelle je Vertragsstand. Rechnungen aus `computeCashflowForecast` (dieselbe Berechnung wie die Seite
 * Cashflow, hier netto, 53 Wochen). Ohne Spaltenwahl (P2-V3 § 5).
 */
import { FC } from 'react';
import { Alert, Box, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChartCard from '../../../components/auswertungen/ChartCard';
import InfoTip from '../../../components/InfoTip';
import SortableTable, { Spalte } from '../../../components/auswertungen/SortableTable';
import { breiteAus } from '../../../components/auswertungen/spaltenwahl';
import { Hinweis, int, Kachel, Laden, tEuro, useBreitePx, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import { formatEur, formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchPrognoseGeteilt, VertragsstandZeile } from '../../../helpers/auswertungenData';
import { fetchZahlungseingangGeteilt } from '../../../helpers/umsatzData';
import { delta } from './befund';
import { PlanMarke } from './KopfKacheln';
import { DatenstandText, Kartenkopf } from './teile';

const STAND: Record<VertragsstandZeile['key'], { label: string; tip: string }> = {
  laeuft_zahlt: TERMS.vertragLaeuftZahlt,
  gekuendigt: TERMS.vertragGekuendigt,
  laeuft_ohne_zahlung: TERMS.vertragOhneZahlung,
};

/** „Montag, 19.10.2026". */
const wochentagDatum = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  const tag = d.toLocaleDateString('de-DE', { weekday: 'long', timeZone: 'UTC' });
  return `${tag}, ${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
};

type Zeile = VertragsstandZeile & { alle?: boolean };

const ZahlungseingangKachel: FC = () => {
  // C7 (Persona-Umbau 28.10.2026): dieselbe Antwort wie die Kopfkachel, mit Status „unter/über/im Plan" (Pfeil + Wort + Farbe).
  const { data, fehler } = useLaden(fetchZahlungseingangGeteilt);
  const wert = !data ? (
    fehler ? (
      '—'
    ) : (
      '…'
    )
  ) : data.drift_pct === null ? (
    '—'
  ) : (
    <>
      {delta(data.drift_pct, 1)}
      <PlanMarke drift={data.drift_pct} />
    </>
  );
  const sub = !data ? (
    fehler ? (
      'konnte nicht geladen werden'
    ) : (
      'wird geladen (rechnet einige Sekunden)'
    )
  ) : (
    <>
      {data.drift_pct === null ? 'Quartal hat gerade begonnen' : `nach ${int(data.completed_weeks)} von ${int(data.total_weeks)} Wochen`} · Plan nach erwarteten
      Ausfällen ·{' '}
      <Link component={RouterLink} to="/auswertungen/umsatz/cashflow?tab=history" underline="hover">
        Cashflow →
      </Link>
      {data.stichtag_hinweis && <InfoTip text={data.stichtag_hinweis} />}
    </>
  );
  return <Kachel wert={wert} label={TERMS.zahlungseingangGegenPlan.label} tip={TERMS.zahlungseingangGegenPlan.tip} sub={sub} />;
};

/**
 * Fußnote „ohne nächste Rechnung" mit den Gründen aus der API (Persona-Check 26.10.2026, B03): unbezahlte Rechnung nach der
 * Testphase; Rechnung diese Woche fällig, aber noch nicht verbucht (die Prognose verwirft eine Abrechnungswoche vor ihrem
 * Beginn — Logik der Cashflow-Seite, bewusst unverändert); veralteter Abrechnungsstand; übrige.
 */
export const ohneSatz = (o: { gesamt: number; rechnung_unbezahlt: number; faellig_unverbucht?: number; abrechnung_veraltet?: number }): string => {
  const faellig = o.faellig_unverbucht ?? 0;
  const veraltet = o.abrechnung_veraltet ?? 0;
  const rest = o.gesamt - o.rechnung_unbezahlt - faellig - veraltet;
  const teile = [
    o.rechnung_unbezahlt > 0 ? `${int(o.rechnung_unbezahlt)} mit einer Rechnung nach der Testphase, die nicht bezahlt ist (für sie rechnet die Prognose keine weitere)` : null,
    faellig > 0 ? `${int(faellig)}, deren Rechnung jetzt fällig, aber noch nicht verbucht ist (die Prognose zählt erst ab dieser Woche)` : null,
    veraltet > 0 ? `${int(veraltet)} mit veraltetem Abrechnungsstand` : null,
    rest > 0 ? `${int(rest)} ohne Rechnungstermin in den nächsten Wochen` : null,
  ].filter(Boolean);
  return `${int(o.gesamt)} von ihnen haben keine nächste Rechnung in der Prognose${teile.length ? `: ${teile.join('; ')}` : ''}.`;
};

const NaechsteRechnungenKarte: FC = () => {
  const { data, fehler } = useLaden(fetchPrognoseGeteilt);
  const c = data?.cashflow;
  const st = data?.vertragsstand ?? [];
  const alle: Zeile = {
    key: 'laeuft_zahlt',
    alle: true,
    vertraege: st.reduce((s, x) => s + x.vertraege, 0),
    mit_rechnung: st.reduce((s, x) => s + x.mit_rechnung, 0),
    summe: st.reduce((s, x) => s + x.summe, 0),
    erwartet: st.reduce((s, x) => s + x.erwartet, 0),
  };
  // Kartenbreite (keine Spaltenwahl, P2-V3 § 5): schmal (< 640 px) stehen Verträge und „voraussichtlich bezahlt" als
  // Unterzeilen — fünf Spalten liefen bei 267 und 414 px waagrecht über (Abnahme 25.10.2026).
  const [breiteRef, breitePx] = useBreitePx();
  const schmal = breitePx !== null && breiteAus(breitePx) === 'schmal';
  const unter = (s: string) => (
    <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400, whiteSpace: 'normal' }}>
      {s}
    </Box>
  );
  const zaehler = (r: Zeile) => `${int(r.vertraege)} Verträge · ${int(r.mit_rechnung)} mit Rechnung`;
  const spalten: Spalte<Zeile>[] = [
    {
      key: 'stand',
      label: TERMS.vertragsstand.label,
      tip: TERMS.vertragsstand.tip,
      render: (r) => (
        <Box component="span" sx={{ lineHeight: 1.3 }}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {STAND[r.key].label}
            <InfoTip text={STAND[r.key].tip} />
          </Box>
          {schmal && unter(zaehler(r))}
        </Box>
      ),
      summary: schmal ? (
        <Box component="span" sx={{ lineHeight: 1.3 }}>
          Alle laufenden Verträge
          {unter(zaehler(alle))}
        </Box>
      ) : undefined,
      minWidth: schmal ? undefined : 160,
    },
    ...(schmal
      ? []
      : ([
          { key: 'v', label: 'Verträge', align: 'right', render: (r) => int(r.vertraege), summary: int(alle.vertraege) },
          { key: 'mit', label: TERMS.laufendeVertraege.short, tip: TERMS.laufendeVertraege.tip, align: 'right', render: (r) => int(r.mit_rechnung), summary: int(alle.mit_rechnung) },
        ] as Spalte<Zeile>[])),
    {
      key: 'sum',
      label: TERMS.naechsteRechnungenSumme.label,
      tip: TERMS.naechsteRechnungenSumme.tip,
      einheit: 'netto',
      align: 'right',
      render: (r) => (
        <Box component="span" sx={{ lineHeight: 1.25 }}>
          {tEuro(r.summe)}
          {schmal && unter(`voraussichtlich ${tEuro(r.erwartet)}`)}
        </Box>
      ),
      summary: (
        <Box component="span" sx={{ lineHeight: 1.25 }}>
          {tEuro(alle.summe)}
          {schmal && unter(`voraussichtlich ${tEuro(alle.erwartet)}`)}
        </Box>
      ),
    },
    ...(schmal
      ? []
      : ([
          {
            key: 'erw',
            label: TERMS.voraussichtlichBezahlt.short,
            tip: TERMS.voraussichtlichBezahlt.tip,
            einheit: 'netto',
            align: 'right',
            render: (r) => tEuro(r.erwartet),
            summary: tEuro(alle.erwartet),
          },
        ] as Spalte<Zeile>[])),
  ];
  const o = data?.ohne_naechste_rechnung;
  return (
    <ChartCard
      title="Die nächste Rechnung jedes laufenden Vertrags"
      howToRead="Für jeden laufenden Vertrag die nächste Rechnung, netto, und wie viel davon voraussichtlich bezahlt wird. Eine Rechnung je Vertrag, nicht der Umsatz eines Jahres. Dieselbe Berechnung wie die Seite Cashflow, die 13 Wochen und brutto zeigt."
      terms={[
        { label: TERMS.laufendeVertraege.label, tip: TERMS.laufendeVertraege.tip },
        { label: TERMS.naechsteRechnungenSumme.label, tip: TERMS.naechsteRechnungenSumme.tip },
        { label: TERMS.voraussichtlichBezahlt.label, tip: TERMS.voraussichtlichBezahlt.tip },
        { label: TERMS.zahlungseingangGegenPlan.label, tip: TERMS.zahlungseingangGegenPlan.tip },
        { label: TERMS.vertragsstand.label, tip: TERMS.vertragsstand.tip },
      ]}
      source="Cashflow-Prognose (laufende Verträge aus PL-X), Zahlungs-Schnappschüsse"
      freshnessKey={['plx_orders']}
    >
      {!data || !c ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          <Kartenkopf>
            Rechnungen ab {wochentagDatum(data.prognose.von)}, für {int(data.prognose.wochen)} Wochen · Vertragsstand: <DatenstandText ds={data.datenstand} />
          </Kartenkopf>
          {data.stichtag_hinweis && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {data.stichtag_hinweis}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 2, mb: 2.5 }}>
            <Kachel
              wert={int(c.orders)}
              label={TERMS.laufendeVertraege.label}
              tip={TERMS.laufendeVertraege.tip}
              sub={`Rechnung in den ${int(data.prognose.wochen)} Wochen ab dieser Woche`}
            />
            <Kachel
              wert={tEuro(c.sum_net)}
              label={TERMS.naechsteRechnungenSumme.label}
              tip={TERMS.naechsteRechnungenSumme.tip}
              sub={`eine Rechnung je Vertrag · Ø ${formatEur(c.orders > 0 ? c.sum_net / c.orders : null, 0)}`}
            />
            <Kachel
              wert={tEuro(c.expected_net)}
              label={TERMS.voraussichtlichBezahlt.label}
              tip={TERMS.voraussichtlichBezahlt.tip}
              sub={`${formatPct(c.sum_net > 0 ? (c.expected_net / c.sum_net) * 100 : null, 0)} · gewichtet nach Mahnstufe; Verträge in der Testphase mit der erwarteten Wandlungsrate je Produkt`}
            />
            <ZahlungseingangKachel />
          </Box>
          <Box ref={breiteRef}>
            <SortableTable rows={st} columns={spalten} rowKey={(r) => r.key} maxHeight={null} eng={schmal} summaryLabel="Alle laufenden Verträge" />
          </Box>
          <Hinweis>
            Netto (ohne MwSt.). Laufende Verträge sind Abo-Anfos ab 2024 mit Vertragsstand „läuft“ — auch gekündigte bis zum Laufzeitende —, ohne
            Einmalkäufe.
            {o && o.gesamt > 0 && ` ${ohneSatz(o)}`}{' '}
            {c.outside_orders > 0 && ` ${int(c.outside_orders)} Rechnungen gehören zu Bestellungen vor 2024 oder außerhalb der Zählung.`} Die Seite
            Cashflow zeigt dieselbe Rechenregel für 13 Wochen und brutto; die Zahlen sind deshalb nicht gleich.
          </Hinweis>
        </>
      )}
    </ChartCard>
  );
};

export default NaechsteRechnungenKarte;
